import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "../../../Translation";
import { Cog8ToothIcon, DocumentIcon, ExclamationCircleIcon, TagIcon } from "@heroicons/react/24/outline";
import { TextArea } from "../../Inputs/TextArea";
import { Button } from "../../Buttons";
import Tabs, { useTabs } from "../../Tabs";
import { useAPI } from "../../../API";
import Select from "../../Inputs/Select";
import { bgColor, hoverBgColor, textColor } from "../../../Resources/CSS/styling";
import Radio from "../../Inputs/Radio";


const ConfigurationTab = ({ tmpStep, setTmpStep, save, error, isCreate, tags = [] }) => {

    const { translate } = useTranslation();
    const api = useAPI();


    const actionOptions = [
        { id: 'add', name: translate('automatisation.add') },
        { id: 'remove', name: translate('automatisation.remove') },
    ]

    const selectedAction =  tmpStep.data.is_deletion ? actionOptions[1] : actionOptions[0];

    const setAction = (e) => {
        setTmpStep({ ...tmpStep, data: { ...tmpStep.data, is_deletion: e === 'remove' } });
    }

    const setTag = (tag) => {
        setTmpStep({ ...tmpStep, data: { ...tmpStep.data, tag_id: tag.id } });
    }

    const selectedTag = tags.length > 0 ? tags.find((tag) => tag.id === parseInt(tmpStep.data.tag_id)) || tags[0] : null;

    useEffect(() => {
        if (tmpStep && tmpStep.data && !tmpStep.data.tag_id && tags.length > 0) {
            setTmpStep({ ...tmpStep, data: { ...tmpStep.data, tag_id: tags[0].id } });
        }
    }, [tmpStep, tags])
  
    return (
        <div className="flex flex-col mt-4 overflow-visible">

            {error && <p className="text-red-600 flex flex-row items-center bg-red-50 p-4"><ExclamationCircleIcon className="w-6 h-6 mr-2" />{error}</p>}
            <p className="text-xl text-gray-600 font-bold mt-2">{translate('automatisation.action')}</p>
            <Radio selected={selectedAction} options={actionOptions} onChange={(e) => setAction(e)} />

            <p className="text-xl text-gray-600 font-bold mt-2">{translate('automatisation.tag')}</p>
            {tags.length > 0 && <Select selected={selectedTag} options={tags} setSelected={(e) => setTag(e)} />}   

            {!isCreate && <div className="flex flex-row w-full flex-1 justify-end mt-4">
                <div className="flex flex-row">
                    <Button className="bg-black hover:bg-gray-800" onClick={() => save(tmpStep)}>
                        {translate('save')}
                    </Button>
                </div>
            </div>}
        </div>
    )
}

const DescriptionTab = ({ tmpStep, setTmpStep, save, isCreate }) => {
    const { translate } = useTranslation();
    return (
        <div className="flex flex-col mt-4">
            <p className="text-xl text-gray-600 font-bold">{translate('automatisation.description')}</p>
            <TextArea rows={5} value={tmpStep.description} onChange={(e) => setTmpStep({ ...tmpStep, description: e.target.value })} />

            {!isCreate && <div className="flex flex-row w-full flex-1 justify-end mt-4">
                <div className="flex flex-row">
                    <Button className="bg-black hover:bg-gray-800" onClick={() => save(tmpStep, JSON.stringify(tmpStep.data.body))}>
                        {translate('save')}
                    </Button>
                </div>
            </div>}
        </div>
    )
}

export default function TagPanel({ setStepToClone, panelHook, step, refresh, deleteStep, isCreate, onCreateNewStep }) {
    const { translate } = useTranslation();
    const [isExpanded, setIsExpanded] = useState(false);
    const [tmpStep, setTmpStep] = useState({ ...step });
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const api = useAPI();

    const tabs = useTabs([
        { icon: Cog8ToothIcon, name: translate('automatisation.configuration'), current: true },
        { icon: DocumentIcon, name: translate('automatisation.description'), current: false }
    ]);
    
    const [tags, setTags] = useState([]);

    useEffect(() => {
        api.listTags(1,99999).then((result) => setTags(result.data.tags));
    }, []);
    // Reset tmpStep when panel is closed
    useEffect(() => {
        if (!panelHook.open) {
            setTmpStep({ ...step });
        }
    }, [panelHook.open]);

    const save = async (tmpStep) => {


        try {
            if (isCreate) {
                if (!tmpStep.description || tmpStep.description.trim() === '') {
                    const action = tmpStep.data.is_deletion ? "Remove" : "Add";
                    const tag = tags.find((tag) => tag.id === parseInt(tmpStep.data.tag_id));
                    tmpStep.description = action + " contact from tag " + (tag ? tag.name : tmpStep.data.tag_id);
                }

                const newStepResponse = await api.createAutomatisationStep(tmpStep.automatisation_id, tmpStep);
                if (newStepResponse.data.automatisation_step) {
                    onCreateNewStep(newStepResponse.data.automatisation_step);
                }
            } else {
                await api.updateAutomatisationStep(tmpStep.automatisation_id, tmpStep);
            }

            panelHook.setOpen(false);
            setTimeout(() => {
                refresh();
            }, 1000);
        } catch (e) {
            setError('Invalid JSON');
            return;
        }

    }

    const selectStepToClone = (step) => {
        setStepToClone(step);
        panelHook.setOpen(false);
    }

    return (
        <div className="flex flex-col flex-1 rounded-md p-4 overflow-hidden h-full w-96">
            <div className="flex flex-row items-center">
                <TagIcon className={`w-12 h-12 min-w-12 min-h-12 aspect-square border p-2 bg-gray-50 rounded-md mr-2 ${textColor}`} />
                <div className="flex flex-col ml-2">
                    <h2 className="text-xl font-bold">{translate('automatisation.tag')}</h2>
                </div>
                
                <div className="flex flex-row flex-1 justify-end">

                {!isCreate && <Button className="bg-red-600 hover:bg-red-300 ml-4" onClick={deleteStep}>
                        {translate('delete')}
                    </Button>}
                {isCreate && <Button className={`${bgColor} ${hoverBgColor} ml-4 opacity-100`} onClick={() => save(tmpStep)}>{translate('create')}</Button>}
                {!isCreate && <Button className={`${bgColor} ${hoverBgColor} ml-4 opacity-100`} onClick={() => selectStepToClone(tmpStep)}>{translate('clone')}</Button>}
                </div>
            </div>

            <div className="flex flex-row overflow-x-scroll no-scrollbar">
                <Tabs tabs={tabs} />
            </div>

            {tabs.currentTabIndex === 0 && <ConfigurationTab tmpStep={tmpStep} setTmpStep={setTmpStep} panelHook={panelHook} error={error} save={save} isCreate={isCreate} tags={tags} />}
            {tabs.currentTabIndex === 1 && <DescriptionTab tmpStep={tmpStep} setTmpStep={setTmpStep} panelHook={panelHook} error={error} save={save} isCreate={isCreate} />}
        </div>
    )
}